import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Save } from 'lucide-react';
import { DASH, authHeader } from '../api';
import { SectionCard, Spinner, PrimaryButton, fmtMoney } from '../ui';
import ConfirmDialog from '../../components/ConfirmDialog';

const PriceField = ({ testId, label, value, onChange, enabled, onToggle, hasToggle }) => (
  <div className="flex items-center justify-between gap-4 py-4 border-b border-[#f0ebe0] last:border-0">
    <div>
      <div className="font-semibold text-[#0d1f15]">{label}</div>
      {hasToggle && (
        <label className="flex items-center gap-2 mt-1 text-xs text-[#5a7062] cursor-pointer">
          <input type="checkbox" data-testid={`${testId}-toggle`} checked={enabled} onChange={(e) => onToggle(e.target.checked)} />
          Enabled for checkout
        </label>
      )}
    </div>
    <div className="flex items-center gap-1">
      <span className="text-[#5a7062]">$</span>
      <input
        data-testid={testId}
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-28 h-11 px-3 border border-[#d8cfb4] rounded-lg text-right font-semibold text-[#0d1f15] focus:outline-none focus:border-[#d97736] focus:ring-2 focus:ring-[#d97736]/20"
      />
    </div>
  </div>
);

const PricingSection = () => {
  const [pricing, setPricing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${DASH}/pricing`, { headers: authHeader() })
      .then((res) => { setPricing(res.data); setDraft(res.data); })
      .catch(() => setError('Could not load pricing.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = pricing && draft && JSON.stringify(pricing) !== JSON.stringify(draft);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const body = {
        single_price: parseFloat(draft.single_price),
        pack_price: parseFloat(draft.pack_price),
        pack_enabled: draft.pack_enabled,
        membership_price: parseFloat(draft.membership_price),
        membership_enabled: draft.membership_enabled,
      };
      const res = await axios.patch(`${DASH}/pricing`, body, { headers: authHeader() });
      setPricing(res.data);
      setDraft(res.data);
      setSuccess('Pricing updated — live on checkout now.');
      setConfirmOpen(false);
      setTimeout(() => setSuccess(''), 4000);
    } catch {
      setError('Could not save pricing changes.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !draft) return <Spinner label="Loading pricing…" />;

  return (
    <div data-testid="wellness-admin-section-pricing" className="space-y-4">
      <div>
        <h2 className="text-2xl tracking-tight" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, color: '#0d1f15' }}>
          Pricing Control
        </h2>
        <p className="text-sm text-[#5a7062] mt-1">Changes take effect immediately on the live booking checkout.</p>
      </div>
      {error && <div data-testid="pricing-error" className="text-sm text-red-700">{error}</div>}
      {success && <div data-testid="pricing-success" className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{success}</div>}
      <SectionCard
        testId="pricing-card"
        actions={
          <PrimaryButton testId="pricing-save-button" disabled={!dirty || saving} onClick={() => setConfirmOpen(true)}>
            <Save size={15} /> Save Changes
          </PrimaryButton>
        }
      >
        <PriceField testId="pricing-single" label="Single Session" value={draft.single_price} onChange={(v) => setDraft({ ...draft, single_price: v })} />
        <PriceField testId="pricing-pack" label="2-Session Pack" value={draft.pack_price} onChange={(v) => setDraft({ ...draft, pack_price: v })} hasToggle enabled={draft.pack_enabled} onToggle={(v) => setDraft({ ...draft, pack_enabled: v })} />
        <PriceField testId="pricing-membership" label="Wellness Membership (/mo)" value={draft.membership_price} onChange={(v) => setDraft({ ...draft, membership_price: v })} hasToggle enabled={draft.membership_enabled} onToggle={(v) => setDraft({ ...draft, membership_enabled: v })} />
      </SectionCard>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm price change?"
        message={
          <div className="space-y-1">
            <p>These new prices go live on the booking checkout immediately:</p>
            <ul className="list-disc list-inside">
              <li>Single: {fmtMoney(draft.single_price)}</li>
              <li>2-Pack: {fmtMoney(draft.pack_price)} {!draft.pack_enabled && '(disabled)'}</li>
              <li>Membership: {fmtMoney(draft.membership_price)}/mo {!draft.membership_enabled && '(disabled)'}</li>
            </ul>
          </div>
        }
        confirmLabel="Yes, Update Prices"
        accent="primary"
        busy={saving}
        onConfirm={save}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default PricingSection;
